import { Link } from '@inertiajs/react';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

type BreadcrumbItem = {
    label: string;
    href?: string;
};

interface PageHeroProps {
    title: string;
    description: string;
    breadcrumbs: BreadcrumbItem[];
    image: string;
    imageAlt: string;
    icon?: LucideIcon;
    actions?: ReactNode;
}

export default function PageHero({ title, description, breadcrumbs, image, imageAlt, icon: Icon, actions }: PageHeroProps) {
    return (
        <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
            <img src={image} alt={imageAlt} className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
            <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
            <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-yellow-500/5 blur-[100px]" />

            <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                <div className="mb-6 flex flex-wrap items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                    {breadcrumbs.map((item, index) => (
                        <div key={`${item.label}-${index}`} className="flex items-center gap-2">
                            {item.href ? (
                                <Link href={item.href} prefetch className="transition-colors hover:text-emerald-300">
                                    {item.label}
                                </Link>
                            ) : (
                                <span className="text-white/80">{item.label}</span>
                            )}
                            {index < breadcrumbs.length - 1 ? <ChevronRight className="size-4" aria-hidden="true" /> : null}
                        </div>
                    ))}
                </div>

                {Icon ? (
                    <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-[#21d36b] shadow-lg backdrop-blur-sm">
                        <Icon className="h-7 w-7" aria-hidden="true" />
                    </div>
                ) : null}

                <h1 className="max-w-4xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">{title}</h1>
                <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">{description}</p>

                {actions ? <div className="mt-8 flex flex-wrap gap-3">{actions}</div> : null}
            </div>
        </div>
    );
}
